import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FaCalendar,
  FaClock,
  FaArrowRight,
  FaSpider,
  FaCode,
  FaDatabase,
} from "react-icons/fa";
import { blogPostsWithSlugs } from "./blogdata";
import Seo from "./Seo";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.6,
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

const Blog = () => {
  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Featured post = first post
  const featuredPost = blogPostsWithSlugs[0];
  const remainingPosts = blogPostsWithSlugs.slice(1);

  return (
    <div className="relative min-h-screen bg-dark-900/40 text-light-100 overflow-hidden">
      <Seo
        title="Web Scraping & Backend Developer Blog Nepal | Aashish Timsina"
        description="Practical web scraping tutorials, Node.js backend guides, and data extraction tips by Aashish Timsina — a web scraper and backend developer based in Nepal."
        path="/blog"
        type="blog"
      />

      {/* Ambient background glows */}

      <div className="relative z-10">
        {/* ── Hero Header ── */}
        <motion.section
          className="py-24 px-6"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="container mx-auto text-center">
            {/* Icon badge */}
            <motion.div
              className="flex justify-center mb-6"
              variants={itemVariants}
            >
              <div className="p-4 rounded-full bg-gradient-to-r from-primary-500 to-pink-500 shadow-lg shadow-primary-500/20">
                <FaCode className="h-10 w-10 text-white" />
              </div>
            </motion.div>

            <motion.span
              className="text-xs uppercase tracking-widest text-primary-400 mb-4 block"
              variants={itemVariants}
            >
              Web Scraper & Backend Developer in Nepal
            </motion.span>

            <motion.h1
              className="text-5xl md:text-6xl font-bold font-space-grotesk mb-6"
              variants={itemVariants}
            >
              <span className="gradient-text">Scraping & Backend</span>
              <span className="text-light-100"> Dev Blog</span>
            </motion.h1>

            <motion.p
              className="text-xl text-light-200 max-w-2xl mx-auto leading-relaxed"
              variants={itemVariants}
            >
              Practical tutorials on web scraping, Node.js backend development,
              data extraction pipelines, and automation — written by a backend
              developer based in Nepal.
            </motion.p>

            {/* Topic pills */}
            <motion.div
              className="flex flex-wrap justify-center gap-3 mt-10"
              variants={itemVariants}
            >
              {[
                { icon: <FaSpider className="mr-1" />, label: "Web Scraping" },
                {
                  icon: <FaCode className="mr-1" />,
                  label: "Node.js & Express",
                },
                {
                  icon: <FaDatabase className="mr-1" />,
                  label: "Data Pipelines",
                },
                {
                  icon: <FaSpider className="mr-1" />,
                  label: "Anti-Bot Bypass",
                },
                {
                  icon: <FaCode className="mr-1" />,
                  label: "Puppeteer & Playwright",
                },
              ].map((pill, i) => (
                <span
                  key={i}
                  className="flex items-center px-4 py-2 rounded-full text-sm bg-dark-800 border border-primary-500/20 text-primary-400"
                >
                  {pill.icon}
                  {pill.label}
                </span>
              ))}
            </motion.div>
          </div>
        </motion.section>

        {/* ── Featured Post ── */}
        {featuredPost && (
          <motion.section
            className="px-6 pb-16"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            <div className="container mx-auto">
              <motion.div
                className="flex items-center gap-3 mb-8"
                variants={itemVariants}
              >
                <div className="h-px flex-1 bg-primary-500/20" />
                <span className="text-xs uppercase tracking-widest text-primary-400 px-3">
                  Featured Post
                </span>
                <div className="h-px flex-1 bg-primary-500/20" />
              </motion.div>

              <motion.div variants={itemVariants}>
                <Link to={`/blog/${featuredPost.slug}`}>
                  <div className="group relative p-8 md:p-10 rounded-lg border border-primary-500/20 bg-gradient-to-br from-dark-800/80 to-dark-700/40 backdrop-blur-sm hover:border-primary-500/50 transition-all duration-300 hover:shadow-2xl hover:shadow-primary-500/10">
                    {/* Glow */}
                    <div className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-10 transition-opacity duration-300 bg-gradient-to-br from-primary-500 to-pink-500" />

                    <div className="relative z-10 flex flex-col md:flex-row gap-8">
                      {/* Left — featured label + content */}
                      <div className="flex-1">
                        <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-primary-500/20 text-primary-400 border border-primary-500/30 mb-4">
                          ✦ Featured
                        </span>
                        <h2 className="text-2xl md:text-3xl font-bold font-space-grotesk mb-4 text-light-100 group-hover:text-primary-400 transition-colors leading-tight">
                          {featuredPost.title}
                        </h2>
                        <p className="text-light-200 leading-relaxed mb-6 text-base md:text-lg line-clamp-3">
                          {featuredPost.excerpt}
                        </p>

                        {/* Meta */}
                        <div className="flex flex-wrap items-center gap-4 text-sm text-light-300 mb-6">
                          <div className="flex items-center gap-2">
                            <FaCalendar className="text-primary-400" />
                            <span>{formatDate(featuredPost.date)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <FaClock className="text-primary-400" />
                            <span>{featuredPost.readTime}</span>
                          </div>
                        </div>

                        {/* Tags */}
                        <div className="flex flex-wrap gap-2 mb-6">
                          {featuredPost.tags.slice(0, 4).map((tag, i) => (
                            <span
                              key={i}
                              className="px-3 py-1 bg-primary-500/10 text-primary-400 border border-primary-500/20 rounded-full text-xs"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>

                        <div className="flex items-center gap-2 text-primary-400 group-hover:text-primary-300 transition-colors font-medium">
                          <span>Read Full Article</span>
                          <FaArrowRight className="group-hover:translate-x-2 transition-transform duration-300" />
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            </div>
          </motion.section>
        )}

        {/* ── All Posts Grid ── */}
        <motion.section
          className="py-8 px-6 pb-20"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          <div className="container mx-auto">
            <motion.div
              className="flex items-center gap-3 mb-10"
              variants={itemVariants}
            >
              <div className="h-px flex-1 bg-primary-500/20" />
              <span className="text-xs uppercase tracking-widest text-primary-400 px-3">
                All Articles
              </span>
              <div className="h-px flex-1 bg-primary-500/20" />
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {remainingPosts.map((post) => (
                <motion.article
                  key={post.id}
                  className="group h-full"
                  variants={itemVariants}
                  transition={{ duration: 0.3 }}
                >
                  <Link to={`/blog/${post.slug}`} className="h-full block">
                    <div className="relative p-6 rounded-lg border border-primary-500/10 bg-gradient-to-br from-dark-800/80 to-dark-700/30 backdrop-blur-sm hover:border-primary-500/30 transition-all duration-300 group-hover:shadow-lg group-hover:shadow-primary-500/10 h-full flex flex-col">
                      {/* Top accent line */}
                      <div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-primary-500/40 to-transparent rounded-full" />

                      {/* Glow */}
                      <div className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-10 transition-opacity duration-300 bg-gradient-to-br from-primary-500/20 to-transparent" />

                      <div className="relative z-10 flex flex-col flex-grow">
                        {/* Tags */}
                        <div className="flex flex-wrap gap-2 mb-4">
                          {post.tags.slice(0, 2).map((tag, i) => (
                            <span
                              key={i}
                              className="px-2 py-1 bg-primary-500/10 text-primary-400 border border-primary-500/20 rounded-full text-xs"
                            >
                              {tag}
                            </span>
                          ))}
                          {post.tags.length > 2 && (
                            <span className="px-2 py-1 bg-pink-500/10 text-pink-400 border border-pink-500/20 rounded-full text-xs">
                              +{post.tags.length - 2}
                            </span>
                          )}
                        </div>

                        <h2 className="text-lg font-bold font-space-grotesk mb-3 text-light-100 group-hover:text-primary-400 transition-colors leading-snug">
                          {post.title}
                        </h2>

                        <p className="text-light-200 text-sm leading-relaxed mb-4 flex-grow line-clamp-3">
                          {post.excerpt}
                        </p>

                        {/* Divider */}
                        <div className="h-px bg-primary-500/10 mb-4" />

                        {/* Meta + Read More */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 text-xs text-light-300">
                            <div className="flex items-center gap-1">
                              <FaCalendar className="text-primary-400" />
                              <span>{formatDate(post.date)}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <FaClock className="text-primary-400" />
                              <span>{post.readTime}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 text-primary-400 text-xs font-medium group-hover:text-primary-300 transition-colors">
                            <span>Read</span>
                            <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          </div>
        </motion.section>

        {/* ── CTA Section ── */}
        <motion.section
          className="py-20 px-6"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <div className="container mx-auto text-center">
            <motion.div
              className="max-w-3xl mx-auto p-10 rounded-lg border border-primary-500/10 bg-gradient-to-br from-dark-800/80 to-dark-700/30 backdrop-blur-sm"
              variants={itemVariants}
            >
              <div className="flex justify-center mb-6">
                <div className="p-3 rounded-full bg-primary-500/10 border border-primary-500/20">
                  <FaSpider className="h-8 w-8 text-primary-400" />
                </div>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold font-space-grotesk mb-4">
                Need a Custom{" "}
                <span className="text-primary-400">Web Scraping Solution?</span>
              </h2>
              <p className="text-lg text-light-200 mb-8 max-w-xl mx-auto">
                Based in Nepal and working with clients worldwide — let's build
                a reliable, automated data extraction pipeline tailored to your
                business needs.
              </p>
              <motion.button
                className="px-8 py-4 bg-gradient-to-r from-primary-500 to-pink-500 text-white font-bold rounded-xl hover:shadow-lg hover:shadow-primary-500/25 transition-all duration-300"
                onClick={() => (window.location.href = "/contact")}
              >
                Get In Touch
              </motion.button>
            </motion.div>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default Blog;
